'use client';

import { LocaleSelector } from '../LocaleSelector';
import { ChevronRight, Loader2 } from 'lucide-react';
import { useApp, useLanguage } from '@/contexts/AppContext';
import { getAdminPageTranslation } from '@/i18n/admin';

const LEGAL_DOCUMENT_LINKS = {
  TERMS_OF_SERVICE: '/legal/terminos-condiciones.pdf',
  PRIVACY_POLICY: '/legal/aviso-privacidad.pdf',
} as const;

interface AdminContentProps {
  isLoggedIn: boolean;
  /** 세션 확인 중 여부 @CODE:ISSUE-150, @CODE:ISSUE-152 optional로 변경하여 타입 일관성 개선 */
  isCheckingSession?: boolean;
  onProfileClick: () => void;
  onOrderHistoryClick: () => void;
  onPaymentMethodClick: () => void;
  onAnnouncementsClick: () => void;
  onPrivacySettingsClick: () => void;
  onLoginClick: () => void;
  onLogoutClick: () => void;
}

export function AdminContent({
  isLoggedIn,
  isCheckingSession = false,
  onProfileClick,
  onOrderHistoryClick,
  onPaymentMethodClick,
  onAnnouncementsClick,
  onPrivacySettingsClick,
  onLoginClick,
  onLogoutClick
}: AdminContentProps) {
  const { language } = useLanguage();
  const { locale } = useApp();
  const t = getAdminPageTranslation(language);
  const localeT = getAdminPageTranslation(locale);
  return (
    <div className="flex-1 pb-24 sm:pb-28 md:pb-32 relative">
      <div className="flex flex-col gap-5 xs:gap-6 sm:gap-8 md:gap-10 items-center py-5 xs:py-6 sm:py-8 md:py-10 w-full max-w-full sm:max-w-xl md:max-w-2xl lg:max-w-4xl xl:max-w-5xl mx-auto px-3 xs:px-4 sm:px-6 md:px-8 lg:px-10">
        {/* Quick Access Buttons */}
        <div className="flex gap-8 xs:gap-10 sm:gap-16 md:gap-20 lg:gap-24 items-start justify-center w-full">
          {/* Profile Button */}
          <button
            onClick={onProfileClick}
            className="group flex flex-col items-center gap-1 xs:gap-1.5 sm:gap-2 transition-all duration-300 hover:scale-105 active:scale-95"
          >
            <div className="relative w-14 xs:w-16 sm:w-20 md:w-[5.3rem] lg:w-24 h-14 xs:h-16 sm:h-20 md:h-[5.3rem] lg:h-24">
              <div className="absolute inset-0 rounded-full bg-muted transition-colors duration-300 group-hover:bg-muted/80" />
              <div className="absolute inset-0 flex items-center justify-center text-primary-hover">
                <svg className="w-7 xs:w-8 sm:w-10 md:w-[2.6rem] lg:w-12 h-7 xs:h-8 sm:h-10 md:h-[2.6rem] lg:h-12" fill="none" viewBox="0 0 35 35">
                  <g clipPath="url(#clip0_profile)">
                    <path d="M17.5109 1.75109C21.7792 1.75109 25.2376 5.20951 25.2376 9.45591C25.2376 13.7023 21.7792 17.1607 17.5109 17.1607C13.2427 17.1607 9.78424 13.7023 9.78424 9.45591C9.78424 5.20951 13.2427 1.75109 17.5109 1.75109ZM17.5109 0C12.2795 0 8.03315 4.22452 8.03315 9.45591C8.03315 14.6873 12.2795 18.9118 17.5109 18.9118C22.7423 18.9118 26.9887 14.6873 26.9887 9.45591C26.9887 4.22452 22.7423 0 17.5109 0Z" fill="currentColor" />
                    <path d="M17.4891 19.0869C26.1789 19.0869 33.227 24.6029 33.227 31.3884V33.2708H1.75109V31.3884C1.75109 24.6029 8.82114 19.0869 17.4891 19.0869ZM17.4891 17.3358C7.83615 17.3358 0 23.6179 0 31.3884V34.212C0 34.6498 0.393996 35.0219 0.875547 35.0219H34.1245C34.606 35.0219 35 34.6717 35 34.212V31.3884C35 23.6398 27.1639 17.3358 17.5109 17.3358H17.4891Z" fill="currentColor" />
                  </g>
                  <defs>
                    <clipPath id="clip0_profile">
                      <rect fill="white" height="35" width="35" />
                    </clipPath>
                  </defs>
                </svg>
              </div>
            </div>
            <span className="xs:text-[10px] sm:text-xs md:text-[0.75rem] lg:text-sm text-primary font-medium text-center whitespace-nowrap text-[12px]">
              {t.myProfile}
            </span>
          </button>

          {/* Order History Button */}
          <button
            onClick={onOrderHistoryClick}
            className="group flex flex-col items-center gap-1 xs:gap-1.5 sm:gap-2 transition-all duration-300 hover:scale-105 active:scale-95"
          >
            <div className="relative w-14 xs:w-16 sm:w-20 md:w-[5.3rem] lg:w-24 h-14 xs:h-16 sm:h-20 md:h-[5.3rem] lg:h-24">
              <div className="absolute inset-0 rounded-full bg-muted transition-colors duration-300 group-hover:bg-muted/80" />
              <div className="absolute inset-0 flex items-center justify-center text-primary-hover">
                <svg className="w-6 xs:w-7 sm:w-9 md:w-[2.2rem] lg:w-11 h-7 xs:h-8 sm:h-10 md:h-[2.6rem] lg:h-12" fill="none" viewBox="0 0 29 35">
                  <g clipPath="url(#clip0_orders)">
                    <path d="M12.4724 21.2602H24.1338" stroke="currentColor" strokeLinecap="round" strokeMiterlimit="10" strokeWidth="0.6" />
                    <path d="M12.4724 15.2394H24.1338" stroke="currentColor" strokeLinecap="round" strokeMiterlimit="10" strokeWidth="0.6" />
                    <path d="M12.4724 9.08633H24.1338" stroke="currentColor" strokeLinecap="round" strokeMiterlimit="10" strokeWidth="0.6" />
                    <path d="M26.3477 1.76434C26.8519 1.76434 27.2464 2.18336 27.2464 2.66856L27.1587 32.133C26.6107 31.8021 25.9531 31.6037 25.3175 31.6037C24.6379 31.6037 23.9803 31.8021 23.4324 32.155L21.7007 33.2136L20.0348 32.155C19.4649 31.8021 18.8073 31.6037 18.1497 31.6037C17.4921 31.6037 16.8125 31.8021 16.2646 32.155L14.5329 33.2136L12.867 32.155C12.2971 31.8021 11.6395 31.6037 10.9819 31.6037C10.3243 31.6037 9.64475 31.8021 9.09675 32.155L7.36508 33.2136L5.67725 32.155C5.10733 31.8021 4.44974 31.6037 3.79214 31.6037C3.15646 31.6037 2.52079 31.7801 1.95087 32.1109H1.79743V2.66856C1.79743 2.16131 2.21391 1.76434 2.69615 1.76434H26.3477ZM26.3477 0H2.65231C1.18367 0 0 1.19093 0 2.66856V32.1109C0 33.1695 0.876795 33.9414 1.81935 33.9414C2.12623 33.9414 2.45503 33.8532 2.73998 33.6768L2.80574 33.6326C3.0907 33.4562 3.4195 33.368 3.72638 33.368C4.05518 33.368 4.4059 33.4562 4.69085 33.6547L6.37869 34.7133C6.66364 34.8897 7.01436 35 7.34316 35C7.67196 35 8.02268 34.9118 8.30763 34.7133L9.97355 33.6547C10.2585 33.4783 10.6092 33.368 10.938 33.368C11.2668 33.368 11.6175 33.4562 11.9025 33.6547L13.5684 34.7133C13.8534 34.8897 14.2041 35 14.5329 35C14.8617 35 15.2124 34.9118 15.4974 34.7133L17.1633 33.6547C17.4482 33.4783 17.7989 33.368 18.1277 33.368C18.4565 33.368 18.8073 33.4562 19.0922 33.6547L20.7581 34.7133C21.0431 34.8897 21.3938 35 21.7226 35C22.0514 35 22.4021 34.9118 22.6871 34.7133L24.3749 33.6547C24.6599 33.4783 25.0106 33.368 25.3394 33.368C25.6682 33.368 25.997 33.4562 26.2819 33.6326C26.5888 33.8091 26.8957 33.8973 27.2245 33.8973C28.167 33.8973 29.0438 33.1474 29.0438 32.0668V2.66856C29.0438 1.19093 27.8602 0 26.3915 0H26.3477Z" fill="currentColor" />
                    <path d="M10.5654 9.59357C10.5654 11.2256 9.25019 12.5488 7.62812 12.5488C6.00605 12.5488 4.69085 11.2256 4.69085 9.59357C4.69085 7.96156 6.00605 6.63831 7.62812 6.63831" stroke="currentColor" strokeLinecap="round" strokeMiterlimit="10" strokeWidth="0.6" />
                    <path d="M6.7294 8.77757L7.7158 10.0347L9.75435 8.138" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="0.6" />
                    <path d="M7.62812 19.0769C8.83371 19.0769 9.82011 20.0693 9.82011 21.2823C9.82011 22.4953 8.83371 23.4877 7.62812 23.4877C6.42252 23.4877 5.43613 22.4953 5.43613 21.2823C5.43613 20.0693 6.42252 19.0769 7.62812 19.0769ZM7.62812 17.7536C5.69917 17.7536 4.12094 19.3195 4.12094 21.2823C4.12094 23.2451 5.67725 24.811 7.62812 24.811C9.57899 24.811 11.1353 23.2451 11.1353 21.2823C11.1353 19.3195 9.57899 17.7536 7.62812 17.7536Z" fill="currentColor" />
                  </g>
                  <defs>
                    <clipPath id="clip0_orders">
                      <rect fill="white" height="35" width="29" />
                    </clipPath>
                  </defs>
                </svg>
              </div>
            </div>
            <span className="xs:text-[10px] sm:text-xs md:text-[0.75rem] lg:text-sm text-primary font-medium text-center whitespace-nowrap text-[12px]">
              {t.myOrders}
            </span>
          </button>
        </div>

        {/* Settings Menu List */}
        <div className="flex flex-col w-full bg-background rounded-[5px] overflow-hidden shadow-sm border border-border/30 transition-all duration-200">
          {/* Preferences */}
          <LocaleSelector
            renderTrigger={(currentLocale) => (
              <button
                type="button"
                className="flex items-center justify-between py-2.5 xs:py-3 sm:py-3.5 md:py-4 lg:py-5 px-3 xs:px-4 sm:px-4.5 md:px-5 lg:px-6 transition-colors duration-200 hover:bg-muted/50 text-left w-full group"
              >
                <span className="text-[13px] xs:text-sm sm:text-base md:text-[1.125rem] lg:text-lg text-foreground font-medium">
                  {t.languageSettings}
                </span>
                <span className="text-[13px] xs:text-sm sm:text-base text-muted-foreground">
                  {currentLocale ? `${currentLocale.languageIcon} ${currentLocale.nativeName}` : ''}
                </span>
              </button>
            )}
          />

          <div className="h-[1px] bg-border" />

          {/* Payment Method */}
          <button
            onClick={onPaymentMethodClick}
            className="flex items-center justify-between py-2.5 xs:py-3 sm:py-3.5 md:py-4 lg:py-5 px-3 xs:px-4 sm:px-4.5 md:px-5 lg:px-6 transition-colors duration-200 hover:bg-muted/50 text-left w-full group"
          >
            <span className="text-[13px] xs:text-sm sm:text-base md:text-[1.125rem] lg:text-lg text-foreground font-medium">
              {t.paymentMethod}
            </span>
            <ChevronRight className="w-3.5 xs:w-4 sm:w-5 md:w-5 lg:w-6 h-3.5 xs:h-4 sm:h-5 md:h-5 lg:h-6 text-muted-foreground transition-transform duration-200 group-hover:translate-x-1 shrink-0" />
          </button>

          <div className="h-[1px] bg-border" />

          {/* Tax Invoice */}
          <button
            onClick={onOrderHistoryClick}
            className="flex items-center justify-between py-2.5 xs:py-3 sm:py-3.5 md:py-4 lg:py-5 px-3 xs:px-4 sm:px-4.5 md:px-5 lg:px-6 transition-colors duration-200 hover:bg-muted/50 text-left w-full group"
          >
            <span className="text-[13px] xs:text-sm sm:text-base md:text-[1.125rem] lg:text-lg text-foreground font-medium">
              {t.taxInvoice}
            </span>
            <ChevronRight className="w-3.5 xs:w-4 sm:w-5 md:w-5 lg:w-6 h-3.5 xs:h-4 sm:h-5 md:h-5 lg:h-6 text-muted-foreground transition-transform duration-200 group-hover:translate-x-1 shrink-0" />
          </button>

          <div className="h-[1px] bg-border" />

          {/* Announcements */}
          <button
            onClick={onAnnouncementsClick}
            className="flex items-center justify-between py-2.5 xs:py-3 sm:py-3.5 md:py-4 lg:py-5 px-3 xs:px-4 sm:px-4.5 md:px-5 lg:px-6 transition-colors duration-200 hover:bg-muted/50 text-left w-full group"
          >
            <span className="text-[13px] xs:text-sm sm:text-base md:text-[1.125rem] lg:text-lg text-foreground font-medium">
              {t.announcements}
            </span>
            <ChevronRight className="w-3.5 xs:w-4 sm:w-5 md:w-5 lg:w-6 h-3.5 xs:h-4 sm:h-5 md:h-5 lg:h-6 text-muted-foreground transition-transform duration-200 group-hover:translate-x-1 shrink-0" />
          </button>

          <div className="h-[1px] bg-border" />

          {/* Terms of Service */}
          <a
            href={LEGAL_DOCUMENT_LINKS.TERMS_OF_SERVICE}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${t.termsOfService} ${t.opensInNewTab}`}
            className="flex items-center justify-between py-2.5 xs:py-3 sm:py-3.5 md:py-4 lg:py-5 px-3 xs:px-4 sm:px-4.5 md:px-5 lg:px-6 transition-colors duration-200 hover:bg-muted/50 text-left w-full group"
          >
            <span className="text-[13px] xs:text-sm sm:text-base md:text-[1.125rem] lg:text-lg text-foreground font-medium">
              {t.termsOfService}
            </span>
            <ChevronRight className="w-3.5 xs:w-4 sm:w-5 md:w-5 lg:w-6 h-3.5 xs:h-4 sm:h-5 md:h-5 lg:h-6 text-muted-foreground transition-transform duration-200 group-hover:translate-x-1 shrink-0" />
          </a>

          <div className="h-[1px] bg-border" />

          {/* Privacy Policy */}
          <a
            href={LEGAL_DOCUMENT_LINKS.PRIVACY_POLICY}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${t.privacyPolicy} ${t.opensInNewTab}`}
            className="flex items-center justify-between py-2.5 xs:py-3 sm:py-3.5 md:py-4 lg:py-5 px-3 xs:px-4 sm:px-4.5 md:px-5 lg:px-6 transition-colors duration-200 hover:bg-muted/50 text-left w-full group"
          >
            <span className="text-[13px] xs:text-sm sm:text-base md:text-[1.125rem] lg:text-lg text-foreground font-medium">
              {t.privacyPolicy}
            </span>
            <ChevronRight className="w-3.5 xs:w-4 sm:w-5 md:w-5 lg:w-6 h-3.5 xs:h-4 sm:h-5 md:h-5 lg:h-6 text-muted-foreground transition-transform duration-200 group-hover:translate-x-1 shrink-0" />
          </a>

          <div className="h-[1px] bg-border" />

          {!isCheckingSession && isLoggedIn ? (
            <>
              {/* Store-scoped partner visibility settings */}
              <button
                type="button"
                onClick={onPrivacySettingsClick}
                className="flex items-center justify-between py-2.5 xs:py-3 sm:py-3.5 md:py-4 lg:py-5 px-3 xs:px-4 sm:px-4.5 md:px-5 lg:px-6 transition-colors duration-200 hover:bg-muted/50 text-left w-full group"
              >
                <span className="text-[13px] xs:text-sm sm:text-base md:text-[1.125rem] lg:text-lg text-foreground font-medium">
                  {localeT.partnerVisibilitySettings}
                </span>
                <ChevronRight className="w-3.5 xs:w-4 sm:w-5 md:w-5 lg:w-6 h-3.5 xs:h-4 sm:h-5 md:h-5 lg:h-6 text-muted-foreground transition-transform duration-200 group-hover:translate-x-1 shrink-0" />
              </button>

              <div className="h-[1px] bg-border" />
            </>
          ) : null}

          {/* Delete Account */}
          <button className="flex items-center justify-between py-2.5 xs:py-3 sm:py-3.5 md:py-4 lg:py-5 px-3 xs:px-4 sm:px-4.5 md:px-5 lg:px-6 transition-colors duration-200 hover:bg-destructive/10 text-left w-full group">
            <span className="text-[13px] xs:text-sm sm:text-base md:text-[1.125rem] lg:text-lg text-foreground font-medium">
              {t.deleteAccount}
            </span>
            <ChevronRight className="w-3.5 xs:w-4 sm:w-5 md:w-5 lg:w-6 h-3.5 xs:h-4 sm:h-5 md:h-5 lg:h-6 text-destructive transition-transform duration-200 group-hover:translate-x-1 shrink-0" />
          </button>
        </div>
      </div>

      {/* Logout Button - Responsive (@CODE:ISSUE-150 세션 확인 중 상태 처리) */}
      <div className="absolute left-1/2 -translate-x-1/2 bottom-20 sm:bottom-24 md:bottom-[120px] w-[200px] sm:w-[220px] md:w-[250px]">
        <button
          onClick={isCheckingSession ? undefined : (isLoggedIn ? onLogoutClick : onLoginClick)}
          disabled={isCheckingSession}
          className={`w-full rounded-[5px] py-2.5 sm:py-3 md:py-[10px] transition-all duration-200 flex items-center justify-center font-[Noto_Sans] ${
            isCheckingSession
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-secondary hover:bg-secondary/90 active:bg-secondary/80 hover:shadow-lg active:scale-95'
          }`}
        >
          {isCheckingSession ? (
            <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin text-foreground" />
          ) : (
            <span className="text-foreground text-xs sm:text-sm md:text-[14px] tracking-[-0.28px] text-[14px] font-bold">
              {isLoggedIn ? t.logOut : t.logIn}
            </span>
          )}
        </button>
      </div>
    </div>
  );
}
